/**
 * Super admin unified audit log: admin actions, WAC recalc, fuel price changes, user soft deletes.
 */
import { Fragment, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import { fetchAdminAuditLogs } from './adminApi';
import './admin-dashboard.css';

const CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'wac', label: 'WAC' },
  { id: 'fuel_price', label: 'Fuel price' },
  { id: 'delete', label: 'Deletes' },
  { id: 'user_delete', label: 'User deletes' },
  { id: 'edit', label: 'Edits' },
  { id: 'admin', label: 'Admin' },
];

const SOURCE_LABELS = {
  admin: 'Admin',
  wac: 'WAC',
  fuel_price: 'Fuel price',
  user_delete: 'User',
};

function severityClass(severity) {
  if (severity === 'critical') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'info';
}

function formatJsonBlock(value) {
  if (value === null || value === undefined || value === '') return null;
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

function DetailList({ fields }) {
  const entries = Object.entries(fields).filter(([, value]) => value !== null && value !== undefined && value !== '');
  if (!entries.length) {
    return <p className="small text-muted mb-0">No additional detail recorded.</p>;
  }

  return (
    <dl className="audit-detail-dl mb-0">
      {entries.map(([label, value]) => (
        <div key={label} className="audit-detail-dl-row">
          <dt>{label}</dt>
          <dd>{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function UserDeleteDetails({ row }) {
  const details = row.details || {};
  const deleted = details.deleted_user || {};
  const deleter = details.deleted_by_user || {};

  return (
    <div className="audit-detail-grid">
      <div className="audit-detail-block">
        <strong className="small d-block mb-1">Deleted user</strong>
        <DetailList
          fields={{
            Name: deleted.name,
            Username: deleted.user_name,
            Email: deleted.email,
            Phone: deleted.phone,
            Role: deleted.role_name,
            'User ID': deleted.id,
            Designation: deleted.designation,
          }}
        />
      </div>
      <div className="audit-detail-block">
        <strong className="small d-block mb-1">Deleted by</strong>
        <DetailList
          fields={{
            Name: deleter.name || row.user_name,
            Username: deleter.user_name,
            Email: deleter.email,
            'User ID': deleter.id || row.user_id,
            When: details.deleted_at || row.created_at,
          }}
        />
      </div>
    </div>
  );
}

function AuditDetails({ row }) {
  if (row.source === 'user_delete') {
    return <UserDeleteDetails row={row} />;
  }

  const details = row.details || {};
  const blocks = [];

  if (details.old_values && Object.keys(details.old_values).length) {
    blocks.push({ label: 'Old values', value: details.old_values });
  }
  if (details.new_values && Object.keys(details.new_values).length) {
    blocks.push({ label: 'New values', value: details.new_values });
  }
  if (details.change_details) {
    blocks.push({ label: 'Change details', value: details.change_details });
  }
  if (details.payload && Object.keys(details.payload).length) {
    blocks.push({ label: 'Payload', value: details.payload });
  }
  if (details.summary && Object.keys(details.summary).length) {
    blocks.push({ label: 'Summary', value: details.summary });
  }
  if (details.tank_snapshot_before) {
    blocks.push({ label: 'Tank snapshot (before)', value: details.tank_snapshot_before });
  }
  if (details.old_price != null || details.new_price != null) {
    blocks.push({
      label: 'Price change',
      value: { old_price: details.old_price, new_price: details.new_price },
    });
  }
  if (details.notes) {
    blocks.push({ label: 'Notes', value: details.notes });
  }
  if (details.message) {
    blocks.push({ label: 'Message', value: details.message });
  }
  if (details.ip_address) {
    blocks.push({ label: 'IP address', value: details.ip_address });
  }

  if (!blocks.length) {
    return <p className="small text-muted mb-0">No additional detail recorded.</p>;
  }

  return (
    <div className="audit-detail-grid">
      {blocks.map((block) => (
        <div key={block.label} className="audit-detail-block">
          <strong className="small d-block mb-1">{block.label}</strong>
          {typeof block.value === 'string' ? (
            <pre className="audit-detail-pre">{block.value}</pre>
          ) : (
            <pre className="audit-detail-pre">{formatJsonBlock(block.value)}</pre>
          )}
        </div>
      ))}
    </div>
  );
}

const AdminAuditLog = (props) => {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [category, setCategory] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [pumpFilter, setPumpFilter] = useState('');
  const [expandedUid, setExpandedUid] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => (profile ? load(1, category) : history.goBack()))
      .catch(() => history.goBack());
  }, [history]);

  function load(page, activeCategory = category) {
    setLoading(true);
    const pumpId = pumpFilter.trim() ? Number(pumpFilter.trim()) : null;
    fetchAdminAuditLogs(page, pumpId, searchFilter, activeCategory)
      .then((data) => {
        setRows(data.data || []);
        setPagination(data.pagination || { current_page: 1, last_page: 1, total: 0 });
        setExpandedUid(null);
      })
      .finally(() => setLoading(false));
  }

  function switchCategory(nextCategory) {
    setCategory(nextCategory);
    load(1, nextCategory);
  }

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={26} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Admin Audit Log" />
          <div className="container pb-3 mt-3">
            <Link to="/admin_dashboard" className="small">← Control Center</Link>

            <div className="admin-panel mt-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Platform audit trail</h5>
                  <p className="small text-muted mb-0">
                    Critical edits, deletes, user soft deletes, WAC recalculations, fuel price changes, and super-admin actions.
                  </p>
                </div>
                <span className="admin-badge active">{pagination.total || 0} events</span>
              </div>

              <ul className="nav nav-pills admin-tab-nav flex-wrap gap-1 mb-3">
                {CATEGORY_TABS.map((tab) => (
                  <li className="nav-item" key={tab.id}>
                    <button
                      type="button"
                      className={`nav-link ${category === tab.id ? 'active' : ''}`}
                      onClick={() => switchCategory(tab.id)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Search action, description, user..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && load(1)}
                  />
                </div>
                <div className="col-md-3">
                  <input
                    className="form-control"
                    placeholder="Pump ID (optional)"
                    value={pumpFilter}
                    onChange={(e) => setPumpFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && load(1)}
                  />
                </div>
                <div className="col-md-2">
                  <button type="button" className="btn btn-primary w-100" onClick={() => load(1)}>
                    Search
                  </button>
                </div>
              </div>

              {loading && <p className="small text-muted">Loading audit events...</p>}

              <div className="table-responsive">
                <table className="table admin-pump-table audit-log-table">
                  <thead>
                    <tr>
                      <th style={{ width: '36px' }} />
                      <th>Time</th>
                      <th>Severity</th>
                      <th>Source</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Pump</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-4">
                          No audit events found for this filter.
                        </td>
                      </tr>
                    )}
                    {rows.map((row) => {
                      const isOpen = expandedUid === row.uid;
                      return (
                        <Fragment key={row.uid}>
                          <tr className={isOpen ? 'audit-row-open' : ''}>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary audit-expand-btn"
                                aria-expanded={isOpen}
                                onClick={() => setExpandedUid(isOpen ? null : row.uid)}
                              >
                                {isOpen ? '−' : '+'}
                              </button>
                            </td>
                            <td className="small text-nowrap">
                              {(row.created_at || '').replace('T', ' ').slice(0, 19)}
                            </td>
                            <td>
                              <span className={`admin-badge audit-severity ${severityClass(row.severity)}`}>
                                {row.severity || 'info'}
                              </span>
                            </td>
                            <td>
                              <span className="admin-badge audit-source">{SOURCE_LABELS[row.source] || row.source}</span>
                            </td>
                            <td className="small">{row.user_name || '-'}</td>
                            <td>
                              <code className="small">{row.action_label || row.action}</code>
                            </td>
                            <td className="small">
                              {row.pump_name || (row.pump_id ? `#${row.pump_id}` : '-')}
                            </td>
                            <td className="small">{row.description || '-'}</td>
                          </tr>
                          {isOpen && (
                            <tr className="audit-detail-row">
                              <td colSpan={8}>
                                <AuditDetails row={row} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pagination.last_page > 1 && (
                <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
                  <span className="small text-muted me-2">
                    Page {pagination.current_page} of {pagination.last_page}
                  </span>
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm ${p === pagination.current_page ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => load(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLog;
